import type { ApplicationVerifier, PhoneMultiFactorInfo } from 'firebase/auth';
import type { Account } from '../types/account';
import type { ApiResponse } from '../types/api';
import type { User } from '../types/auth';
import httpClient from './http/httpClient';

export interface UserData {
  displayName: string;
  clientName: string;
  email: string;
  phoneNumber: string;
  password: string;
  storageType: string;
  billingType: string;
  timezone: string;
  termsAccepted: boolean;
  userType: string;
  invitationCode?: string;
  inviteToken?: string;

  publicKey?: JsonWebKey;

  sharepointConfig?: {
    tenantId: string;
    clientId: string;
    clientSecret: string;
    siteId: string;
    driveId: string;
    basePath: string;
  };
}

export interface KnowledgeBaseFolder {
  path: string;
  lastIndexed?: string;
  status?: 'indexed' | 'indexing' | 'error' | 'not_indexed';
}

export interface ClientData {
  knowledgeBaseFolders?: KnowledgeBaseFolder[];
  clientSettings?: {
    procurement_knowledge?: {
      targets: { path: string; domain: string }[];
    };
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export interface AccountDetails {
  success?: boolean;
  message?: string;
  account: Account;
  client?: ClientData;
  actualDefaultBucketName?: string;
}

export const createUser = async (userData: UserData): Promise<ApiResponse<{ user: User }>> => {
  const response = await httpClient.post<ApiResponse<{ user: User }>>('/createUser', userData);
  return response;
};

export const logoutUser = async (): Promise<ApiResponse<{ message: string }>> => {
  const response = await httpClient.post<ApiResponse<{ message: string }>>('/logout');
  return response;
};

export const validateInvitationToken = async (
  token: string
): Promise<{
  success: boolean;
  valid: boolean;
  email?: string;
  clientName?: string;
  message?: string;
}> => {
  try {
    const response = await httpClient.post<{
      success: boolean;
      valid: boolean;
      email?: string;
      clientName?: string;
      message?: string;
    }>('/validateInvitationToken', { token });
    return response;
  } catch (error: unknown) {
    const err = error as Error & { response?: { data?: { message?: string } } };
    return {
      success: false,
      valid: false,
      message: err.response?.data?.message || err.message,
    };
  }
};

export const getAccountDetails = async (): Promise<AccountDetails> => {
  const response = await httpClient.get<AccountDetails>('/getAccountDetails');
  return response;
};

export const clearUnverifiedPhoneNumber = async (): Promise<ApiResponse<Record<string, never>>> => {
  const response = await httpClient.post<ApiResponse<Record<string, never>>>(
    '/clearUnverifiedPhoneNumber'
  );
  return response;
};

export const sendUserEmailVerification = async (): Promise<void> => {
  const { auth } = await import('./firebase');
  const { sendEmailVerification } = await import('firebase/auth');
  if (auth.currentUser) {
    await sendEmailVerification(auth.currentUser);
  }
};

export const reloadCurrentUser = async (): Promise<boolean> => {
  const { auth } = await import('./firebase');
  if (auth.currentUser) {
    await auth.currentUser.reload();
    return auth.currentUser.emailVerified;
  }
  return false;
};

export const initRecaptchaVerifier = async (container: HTMLElement | string) => {
  const { auth } = await import('./firebase');
  const { RecaptchaVerifier } = await import('firebase/auth');
  return new RecaptchaVerifier(auth, container, {
    size: 'invisible',
  });
};

export const sendPhoneVerification = async (
  phoneNumber: string,
  verifier: ApplicationVerifier
): Promise<string> => {
  const { auth } = await import('./firebase');
  const { PhoneAuthProvider } = await import('firebase/auth');
  const phoneProvider = new PhoneAuthProvider(auth);
  return await phoneProvider.verifyPhoneNumber(phoneNumber, verifier);
};

export const linkPhoneNumber = async (
  verificationId: string,
  verificationCode: string
): Promise<void> => {
  const { auth } = await import('./firebase');
  const { PhoneAuthProvider, linkWithCredential } = await import('firebase/auth');
  if (!auth.currentUser) {
    throw new Error('No user is signed in.');
  }
  const credential = PhoneAuthProvider.credential(verificationId, verificationCode);
  await linkWithCredential(auth.currentUser, credential);
};

export const getMfaStatus = async (): Promise<boolean> => {
  const { auth } = await import('./firebase');
  const { multiFactor } = await import('firebase/auth');
  if (!auth.currentUser) {
    return false;
  }
  return multiFactor(auth.currentUser).enrolledFactors.length > 0;
};

export const getEnrolledMfaPhone = async (): Promise<string | undefined> => {
  const { auth } = await import('./firebase');
  const { multiFactor } = await import('firebase/auth');
  if (!auth.currentUser) {
    return undefined;
  }
  const enrolledFactors = multiFactor(auth.currentUser).enrolledFactors;
  if (enrolledFactors.length > 0) {
    return (enrolledFactors[0] as PhoneMultiFactorInfo).phoneNumber;
  }
  return undefined;
};

export const sendMfaVerification = async (verifier: ApplicationVerifier): Promise<string> => {
  const { auth } = await import('./firebase');
  const { multiFactor, PhoneAuthProvider } = await import('firebase/auth');
  if (!auth.currentUser?.phoneNumber) {
    throw new Error('User not authenticated or phone number is missing.');
  }
  const session = await multiFactor(auth.currentUser).getSession();
  const phoneInfoOptions = {
    phoneNumber: auth.currentUser.phoneNumber,
    session,
  };
  const phoneAuthProvider = new PhoneAuthProvider(auth);
  return await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, verifier);
};

export const enrollMfa = async (
  verificationId: string,
  verificationCode: string
): Promise<void> => {
  const { auth } = await import('./firebase');
  const { multiFactor, PhoneAuthProvider, PhoneMultiFactorGenerator } = await import(
    'firebase/auth'
  );
  if (!auth.currentUser) {
    throw new Error('User not authenticated');
  }

  const cred = PhoneAuthProvider.credential(verificationId, verificationCode);
  const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);
  await multiFactor(auth.currentUser).enroll(multiFactorAssertion, 'Phone');
  await auth.currentUser.reload();
};

export const unenrollMfa = async (): Promise<void> => {
  const { auth } = await import('./firebase');
  const { multiFactor } = await import('firebase/auth');
  if (!auth.currentUser) {
    throw new Error('User not authenticated');
  }

  const enrolledFactors = multiFactor(auth.currentUser).enrolledFactors;
  if (enrolledFactors.length > 0) {
    const mfaInfo = enrolledFactors[0];
    if (mfaInfo) {
      await multiFactor(auth.currentUser).unenroll(mfaInfo);
    }
  }
  await auth.currentUser.reload();
};
